import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LocationProvider } from './context/LocationContext';
import RealtimeProvider from './realtime/RealtimeProvider';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import BookingAlerts from './pages/BookingAlerts';
import BookingAnalytics from './pages/BookingAnalytics';
import Settings from './pages/Settings';
import AMCSettings from './pages/AMCSettings';
import Booking from './pages/Booking';
import BookingSuccess from './pages/BookingSuccess';
import BookingCancelled from './pages/BookingCancelled';
import Bookings from './pages/Bookings';
import PPMRealignment from './pages/PPMRealignment';
import UserManagement from './pages/UserManagement';
import LocationManagement from './pages/LocationManagement';
import AuditLog from './pages/AuditLog';
import BookingLogs from './pages/BookingLogs';
import SearchJobs from './pages/SearchJobs';
import AppointmentAlerts from './pages/AppointmentAlerts';
import SlotManagement from './pages/SlotManagement';
import BookingConflicts from './pages/BookingConflicts';
import DailyReport from './pages/DailyReport';
import LeaveManagement from './pages/LeaveManagement';
import AlertLogs from './pages/AlertLogs';
import TBCJobs from './pages/TBCJobs';
import UnbookedJobs from './pages/UnbookedJobs';
import TechnicianCalendar from './pages/TechnicianCalendar';

// Skip Sentry reporting for non-actionable client errors:
//  - Network errors (no response received): wifi blip / deploy collision / browser sleep.
//    react-query already retries once; remaining failures aren't debuggable from Sentry.
//  - All 401s: auth is binary in this app (logged in or not), so every 401
//    means "session bad → redirect to login." Not a code-level signal.
//  - 409s on /api/booking/: backend's expected "no longer bookable" rejection.
const isNoiseError = (error) => {
  if (!error?.response) return true;
  if (error.response.status === 401) return true;
  if (error.response.status === 409 && (error.config?.url || '').includes('/api/booking/')) return true;
  return false;
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isNoiseError(error)) return;
      Sentry.captureException(error, {
        tags: { type: 'query_error' },
        extra: { queryKey: JSON.stringify(query.queryKey) },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      if (isNoiseError(error)) return;
      Sentry.captureException(error, {
        tags: { type: 'mutation_error' },
        extra: { mutationKey: mutation.options.mutationKey ? JSON.stringify(mutation.options.mutationKey) : undefined },
      });
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <LocationProvider>
            <RealtimeProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/book/:customerId/:jobId" element={<Booking />} />
              <Route path="/book/success" element={<BookingSuccess />} />
              <Route path="/book/cancelled" element={<BookingCancelled />} />

              {/* Protected routes */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/booking-alerts" element={<ProtectedRoute><BookingAlerts /></ProtectedRoute>} />
              <Route path="/reminders" element={<Navigate to="/booking-alerts" replace />} />
              <Route path="/booking-analytics" element={<ProtectedRoute><BookingAnalytics /></ProtectedRoute>} />
              <Route path="/history" element={<Navigate to="/booking-analytics" replace />} />
              <Route path="/amc-settings" element={<ProtectedRoute><AMCSettings /></ProtectedRoute>} />
              <Route path="/child-jobs" element={<Navigate to="/amc-settings" replace />} />
              <Route path="/mechanics" element={<Navigate to="/amc-settings" replace />} />
              <Route path="/job-categories" element={<Navigate to="/amc-settings" replace />} />
              <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
              <Route path="/internal-booking" element={<Navigate to="/bookings" replace />} />
              <Route path="/ppm-realignment" element={<ProtectedRoute><PPMRealignment /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/booking-logs" element={<ProtectedRoute><BookingLogs /></ProtectedRoute>} />
              <Route path="/search-jobs" element={<ProtectedRoute><SearchJobs /></ProtectedRoute>} />
              <Route path="/tbc-search" element={<Navigate to="/search-jobs" replace />} />
              <Route path="/appointment-alerts" element={<ProtectedRoute><AppointmentAlerts /></ProtectedRoute>} />
              <Route path="/booked-reminders" element={<Navigate to="/appointment-alerts" replace />} />
              <Route path="/slot-management" element={<ProtectedRoute><SlotManagement /></ProtectedRoute>} />
              <Route path="/booked-slots" element={<Navigate to="/slot-management" replace />} />
              <Route path="/booking-conflict" element={<ProtectedRoute><BookingConflicts /></ProtectedRoute>} />
              <Route path="/technician-calendar" element={<ProtectedRoute><TechnicianCalendar /></ProtectedRoute>} />
              <Route path="/double-bookings" element={<Navigate to="/booking-conflict" replace />} />
              <Route path="/daily-report" element={<ProtectedRoute><DailyReport /></ProtectedRoute>} />
              <Route path="/leave-management" element={<ProtectedRoute><LeaveManagement /></ProtectedRoute>} />
              <Route path="/tech-day-offs" element={<Navigate to="/leave-management" replace />} />
              <Route path="/alert-logs" element={<ProtectedRoute><AlertLogs /></ProtectedRoute>} />
              <Route path="/reminder-logs" element={<Navigate to="/alert-logs" replace />} />
              <Route path="/tbc-jobs" element={<ProtectedRoute><TBCJobs /></ProtectedRoute>} />
              <Route path="/unbooked-jobs" element={<ProtectedRoute><UnbookedJobs /></ProtectedRoute>} />

              {/* Admin routes (super_admin only) */}
              <Route path="/admin/users" element={<ProtectedRoute requireRole="super_admin"><UserManagement /></ProtectedRoute>} />
              <Route path="/admin/locations" element={<ProtectedRoute requireRole="super_admin"><LocationManagement /></ProtectedRoute>} />
              <Route path="/admin/audit-log" element={<ProtectedRoute requireRole="super_admin"><AuditLog /></ProtectedRoute>} />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </RealtimeProvider>
          </LocationProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
